// /parts/pulls: what has been pulled out of stock for R&D, repair and scrap,
// by project, and the way back for anything pulled by mistake.
//
// A row of the report opens its lines underneath, because "5 pulled, 2 put back"
// only answers the question once somebody can see which pulls those were. The
// Put back key lives on the line, where the units are.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../../components/ui/Button';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import PageHeader from '../../components/PageHeader';
import PageLayout from '../../components/ui/PageLayout';
import { FilterBar, FilterEnd, FilterSearch, FilterSelect } from '../../components/FilterBar';
import { DataTable } from '../../components/table/DataTable';
import ItemName from '../../components/ItemName';
import { Alert } from '../../components/ui/Alert';
import Reveal from '../../components/ui/Reveal';
import { MS } from '../../components/ui/motion';
import { EmptyState } from '../../components/ui/EmptyState';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { Panel, Stat, type StatTone } from '../../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import { fieldClass } from '../../components/ui/Field';
import { Tag } from '../../components/ui/Tag';
import { csvFilename, downloadCsv } from '../../utils/csv';
import { businessToday, shiftDay, formatTimestamp } from '../../utils/date-utils';
import { formatCents } from '../../Procurement/poApi';
import PutBackPopover from './PutBackPopover';
import useUserStore from '../../stores/useUserStore';
import { userCanAccess } from '../../navigation/menuConfig';
import {
  REASON_LABELS,
  usePullsApi,
  type PullLine,
  type Report,
  type ReportFilter,
  type ReportRow,
} from './pullsApi';

const rowKey = (r: ReportRow) => `${r.project ?? ''}\u0000${r.reason}`;
const projectName = (p: string | null) => p || 'No project';
// The server's cap on one lines answer (pulls-service MAX_LINES).
const LINES_CAP = 500;
// Scrap pulls and put-backs of scrap never return to a shelf this way.
const CAN_PUT_BACK = new Set(['rnd', 'repair', 'thrown_out']);

interface ReportMeta {
  openKey: string | null;
  onToggle: (row: ReportRow) => void;
}

const LINES_KEY =
  'rounded-full px-3 py-1 text-xs font-medium text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors duration-150 ease-emphasized motion-reduce:transition-none aria-expanded:bg-surface-container-highest aria-expanded:text-on-surface';

const REPORT_COLUMNS: ColumnDef<ReportRow, any>[] = [
  {
    id: 'project',
    header: 'Project',
    accessorFn: (r) => r.project ?? undefined,
    meta: { cellClass: 'text-on-surface', csv: (r: ReportRow) => r.project ?? '' },
    cell: ({ row }) => projectName(row.original.project),
  },
  {
    id: 'reason',
    header: 'Reason',
    accessorFn: (r) => REASON_LABELS[r.reason],
    cell: ({ row }) => (
      <Tag>{REASON_LABELS[row.original.reason]}</Tag>
    ),
  },
  { id: 'pulled', header: 'Pulled', accessorFn: (r) => r.pulled, meta: { align: 'right', numeric: true } },
  { id: 'put_back', header: 'Put back', accessorFn: (r) => r.put_back, meta: { align: 'right', numeric: true } },
  {
    id: 'net',
    header: 'Net',
    accessorFn: (r) => r.net,
    meta: { align: 'right', numeric: true, cellClass: 'font-semibold text-on-surface' },
  },
  {
    id: 'cost',
    header: 'Cost',
    accessorFn: (r) => r.cost_cents ?? undefined,
    meta: {
      align: 'right',
      numeric: true,
      csv: (r: ReportRow) => (r.cost_cents == null ? '' : r.cost_cents / 100),
    },
    cell: ({ row }) => formatCents(row.original.cost_cents),
  },
  {
    id: 'lines',
    header: () => <span className="sr-only">Lines</span>,
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: (info) => {
      const { openKey, onToggle } = info.table.options.meta as ReportMeta;
      const r = info.row.original;
      const open = openKey === rowKey(r);
      return (
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} lines for ${projectName(r.project)}, ${REASON_LABELS[r.reason]}`}
          onClick={() => onToggle(r)}
          className={LINES_KEY}
        >
          {open ? 'Hide lines' : 'Show lines'}
        </button>
      );
    },
  },
];

interface LinesMeta {
  onPutBack: () => void;
}

const LINE_COLUMNS: ColumnDef<PullLine, any>[] = [
  {
    id: 'when',
    header: 'When',
    accessorFn: (l) => l.occurred_at,
    meta: { cellClass: 'whitespace-nowrap', csv: (l: PullLine) => l.occurred_at },
    cell: ({ row }) => formatTimestamp(row.original.occurred_at, 'MMM D, h:mm A'),
  },
  {
    id: 'item',
    header: 'Item',
    cell: ({ row }) => (
      <>
        <ItemName size="sm" item={row.original} className="max-w-xs" />
        {row.original.barcode && (
          <span className="block font-mono text-[11px] text-on-surface-variant">{row.original.barcode}</span>
        )}
      </>
    ),
  },
  {
    id: 'where',
    header: 'For',
    cell: ({ row }) => {
      const l = row.original;
      return l.serialnumber ? <span className="font-mono">{l.serialnumber}</span> : l.project || '—';
    },
  },
  { id: 'quantity', header: 'Pulled', accessorFn: (l) => l.quantity, meta: { align: 'right', numeric: true } },
  { id: 'put_back', header: 'Put back', accessorFn: (l) => l.put_back, meta: { align: 'right', numeric: true } },
  {
    id: 'from',
    header: 'From',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.from_location || '—',
  },
  {
    id: 'by',
    header: 'By',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.performed_by_name || '—',
  },
  {
    id: 'cost',
    header: 'Cost',
    accessorFn: (l) => l.cost_cents ?? undefined,
    meta: {
      align: 'right',
      numeric: true,
      csv: (l: PullLine) => (l.cost_cents == null ? '' : l.cost_cents / 100),
    },
    cell: ({ row }) => formatCents(row.original.cost_cents),
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Put back</span>,
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: (info) => {
      const { onPutBack } = info.table.options.meta as LinesMeta;
      const l = info.row.original;
      if (!CAN_PUT_BACK.has(l.reason)) return null;
      return <PutBackPopover line={l} onDone={onPutBack} />;
    },
  },
];

const DATE_FIELD = `${fieldClass({ size: 'sm', shape: 'pill' })} m-0 !w-auto px-3 tabular-nums [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

const DateField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <label className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-on-surface-variant">
    <span>{label}</span>
    <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={DATE_FIELD} />
  </label>
);

const Figure: React.FC<{ label: string; value: React.ReactNode; tone?: StatTone }> = ({ label, value, tone }) => (
  <Stat size="sm" label={label} value={value} tone={tone} />
);

const PullsReportPage: React.FC = () => {
  const api = usePullsApi();
  const http = useApi();
  const toast = useToast();

  const today = useMemo(() => businessToday().format('YYYY-MM-DD'), []);
  const [from, setFrom] = useState(() => shiftDay(today, -90));
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState('');
  const user = useUserStore((st) => st.user);
  const [projectText, setProjectText] = useState('');
  const [project, setProject] = useState('');

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState<ReportRow | null>(null);
  const [lines, setLines] = useState<PullLine[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesError, setLinesError] = useState<string | null>(null);
  const linesRef = useRef<HTMLElement>(null);
  const reportAsk = useRef(0);
  const linesAsk = useRef(0);

  // The project box searches as you type; the report asks once you pause.
  useEffect(() => {
    const t = setTimeout(() => setProject(projectText.trim()), 300);
    return () => clearTimeout(t);
  }, [projectText]);

  const filter: ReportFilter = useMemo(() => ({ from, to, reason, search: project }), [from, to, reason, project]);

  const loadReport = useCallback(() => {
    const ask = ++reportAsk.current;
    setLoading(true);
    setError(null);
    api
      .report(filter)
      .then((r) => {
        if (ask === reportAsk.current) setReport(r);
      })
      .catch((err: any) => {
        if (ask === reportAsk.current) setError(err?.message || 'The report did not load.');
      })
      .finally(() => {
        if (ask === reportAsk.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const loadLines = useCallback(
    (row: ReportRow) => {
      const ask = ++linesAsk.current;
      setLinesLoading(true);
      setLinesError(null);
      api
        .lines(row.project ? { from, to, reason: row.reason, project: row.project } : { from, to, reason: row.reason, no_project: '1' })
        .then((r) => {
          if (ask === linesAsk.current) setLines(r.lines || []);
        })
        .catch((err: any) => {
          if (ask === linesAsk.current) setLinesError(err?.message || 'The lines did not load.');
        })
        .finally(() => {
          if (ask === linesAsk.current) setLinesLoading(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [from, to],
  );

  const onToggle = useCallback(
    (row: ReportRow) => {
      if (open && rowKey(open) === rowKey(row)) {
        setOpen(null);
        return;
      }
      setOpen(row);
      setLines([]);
      loadLines(row);
    },
    [open, loadLines],
  );

  // Growth scrolls itself into view: the lines open below the fold otherwise.
  // Waits for the Reveal to mount and grow: the section is not in the tree on the render that opens it.
  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(
      () => linesRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }),
      MS.standard,
    );
    return () => window.clearTimeout(t);
  }, [open]);

  // A new filter changes what the open row means, so close it.
  useEffect(() => {
    setOpen(null);
  }, [from, to, reason, project]);

  const afterPutBack = useCallback(() => {
    if (open) loadLines(open);
    loadReport();
  }, [open, loadLines, loadReport]);

  const rows = report?.rows ?? [];
  const canOpenBin = userCanAccess({ require: 'shopFloor' }, user);
  const hasCost = rows.some((r) => r.cost_cents !== undefined);
  const lineHasCost = lines.some((l) => l.cost_cents !== undefined);
  const scrap = report?.scrap;
  const overThirty = scrap ? (
    <Figure
      label="In the bin over 30 days"
      value={scrap.over_30_days}
      tone={scrap.over_30_days > 0 ? 'warn' : 'neutral'}
    />
  ) : null;

  const exportCsv = async () => {
    try {
      const res: Response = await http.fetch(api.reportCsvPath(filter), { skipJsonParse: true, silent: true });
      downloadCsv(csvFilename('pulls'), await res.text());
      toast.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}.`);
    } catch (err: any) {
      toast.error(err?.message || 'The export did not download. Try again.');
    }
  };

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title="Pulls"
        helpTitle="About pulls"
        lede="Parts taken off the shelf for R&D, repair or scrap, and what came back."
        help={
          <>
            <p>
              A pull takes parts out of stock for something other than a build. This page
              adds them up by project so you can see where the parts went.
            </p>
            <p className="font-medium text-on-surface">The reasons</p>
            <p>
              <span className="text-on-surface">R&amp;D:</span> parts used on a project that
              is not a customer build. Every R&amp;D pull names its project.
            </p>
            <p>
              <span className="text-on-surface">Repair:</span> a part pulled to fix one
              drone. The drone&apos;s serial number is on the pull.
            </p>
            <p>
              <span className="text-on-surface">Scrap:</span> parts moved to the scrap bin.
              They are off the shelf but still on the books until someone throws them out.
            </p>
            <p>
              <span className="text-on-surface">Thrown out:</span> scrap that was physically
              discarded. It leaves the books.
            </p>
            <p className="font-medium text-on-surface">Putting parts back</p>
            <p>
              If a part was pulled and not used, open the project&apos;s lines and press Put
              back on that line. The units return to the shelf they came from and the net
              count drops by that many. A line can only be put back up to what is still out.
            </p>
          </>
        }
        tools={[
          {
            label: 'Export CSV',
            hint: 'The report rows, current filters',
            disabled: loading || rows.length === 0,
            onSelect: exportCsv,
          },
        ]}
      />
      }
    >

      <FilterBar label="Filter pulls">
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
        <FilterSelect label="Reason" value={reason} onChange={setReason}>
          <option value="">All reasons</option>
          <option value="rnd">{REASON_LABELS.rnd}</option>
          <option value="repair">{REASON_LABELS.repair}</option>
          <option value="thrown_out">{REASON_LABELS.thrown_out}</option>
        </FilterSelect>
        <FilterSearch label="Search projects" placeholder="Project" value={projectText} onChange={setProjectText} />
        <FilterEnd>
          <span role="status" className="text-xs tabular-nums text-on-surface-variant">
            {loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
          </span>
        </FilterEnd>
      </FilterBar>

      <Reveal show={!!scrap}>
      {scrap && (
        <Panel label="Scrap summary" padding="sm">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-3 px-1">
          <Figure label="Binned" value={scrap.binned} />
          <Figure label="Thrown out" value={scrap.thrown_out} />
          <Figure label="In bin" value={scrap.in_bin} />
          {canOpenBin ? (
            <Link
              to="/parts/scrap-bin"
              className="rounded-md hover:bg-surface-container-high focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              {overThirty}
            </Link>
          ) : overThirty}
          {scrap.bin_value_cents !== undefined && (
            <Figure label="Bin value" value={formatCents(scrap.bin_value_cents)} />
          )}
          {canOpenBin && <Link
            to="/parts/scrap-bin"
            className={`${buttonClass('quiet')} ml-auto self-center`}
          >
            Open the scrap bin
          </Link>}
          </div>
        </Panel>
      )}
      </Reveal>

      <Reveal show={!!error}>
        <Alert onRetry={loadReport}>{error} Check your connection, then try again.</Alert>
      </Reveal>

      <DataTable<ReportRow>
        data={rows}
        columns={REPORT_COLUMNS}
        getRowId={rowKey}
        meta={{ openKey: open ? rowKey(open) : null, onToggle } as ReportMeta}
        columnVisibility={{ cost: hasCost }}
        sortable
        loading={loading}
        loadingContent={<LogoSpinner label="Loading pulls" />}
        empty={
          <EmptyState variant="area" title="No pulls in this range.">
            Widen the dates, or pull parts from a box or a drone&apos;s page and they will
            show up here.
          </EmptyState>
        }
      />

      <Reveal show={!!open}>
      {open && (
        <section ref={linesRef} aria-label="Pull lines" className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-on-surface">
              {projectName(open.project)}, {REASON_LABELS[open.reason]}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(null)}
              className="rounded-full px-3 py-1 text-xs font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              Hide lines
            </button>
          </div>
          <Reveal show={lines.length >= LINES_CAP}>
            <p className="text-sm text-on-surface-variant">
              Showing the newest {LINES_CAP} lines. Narrow the dates to see older ones.
            </p>
          </Reveal>
          <Reveal show={!!linesError}>
            <Alert variant="text">{linesError} Hide the lines and open them again.</Alert>
          </Reveal>
          <DataTable<PullLine>
            data={lines}
            columns={LINE_COLUMNS}
            getRowId={(l) => l.transaction_id}
            meta={{ onPutBack: afterPutBack } as LinesMeta}
            columnVisibility={{ cost: lineHasCost }}
            loading={linesLoading}
            loadingContent={
              <SkeletonGroup label="Loading lines" className="space-y-2 px-4 py-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </SkeletonGroup>
            }
            empty={<EmptyState variant="area" dense>No lines for this row in the date range.</EmptyState>}
          />
        </section>
      )}
      </Reveal>
    </PageLayout>
  );
};

export default PullsReportPage;
